import Component from "@glimmer/component";
import { service } from "@ember/service";
import { t } from "ember-intl";
import { array } from "@ember/helper";
import { and } from "ember-truth-helpers";
import Entity from "glesys-controlpanel/components/entity";
import EntityField from "glesys-controlpanel/components/entity/fields/field";
import EntityDataCenterField from "glesys-controlpanel/components/entity/fields/data-center";
import EntityList from "glesys-controlpanel/components/entity-list";
import EmptyState from "glesys-controlpanel/components/empty-state";
import CostPlain from "glesys-controlpanel/components/cost/plain";
import humanFileSize from "glesys-controlpanel/helpers/human-file-size";
import GButtonLinkTo from "glesys-controlpanel/components/g-button/link-to";
import IconsPlus from "glesys-controlpanel/components/icons/plus";
import IconsSidebarStorage from "glesys-controlpanel/components/icons/sidebar/storage";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntro from "glesys-controlpanel/components/page-content/service-intro";

export default class FileStorageVolumesOverview extends Component {
  @service abilities;

  get project() {
    return this.args.model.project;
  }

  get sortedVolumes() {
    return this.args.model.fileStorageVolumes.slice().sort((a, b) => b.usedPercentage - a.usedPercentage);
  }

  get canCreate() {
    return this.abilities.can("create file-storage-volume for project", this.project);
  }

  <template>
    <PageContent @isCentered={{true}}>
      <PageContentServiceIntro @title={{t "file-storage-volumes.page-title"}}>
        {{#if (and this.sortedVolumes this.canCreate)}}
          <GButtonLinkTo
            aria-label={{t "file-storage-volumes.create-volume-aria-label"}}
            data-test-button-create-file-storage-volume
            @route="file-storage-volumes.create"
            @color="green"
          >
            <span class="flex items-center">
              <IconsPlus class="w-4 -ml-1 mr-1" />
              <span>{{t "button.create"}}</span>
            </span>
          </GButtonLinkTo>
        {{/if}}
      </PageContentServiceIntro>

      <EntityList @items={{this.sortedVolumes}}>
        <:default as |volume|>
          <Entity
            @route="file-storage-volumes.details"
            @model={{volume.id}}
            @title={{volume.name}}
            @key={{volume.id}}
            @status={{volume.statusIndicator}}
            @linkLabel={{t "file-storage-volumes.manage-volume-aria-label" name=volume.name}}
          >
            <:fields>
              <EntityDataCenterField @dataCenter={{volume.dataCenter}} />
              <EntityField data-test-file-storage-volume-usage @label={{t "label.usage"}}>
                {{humanFileSize volume.used "GiB"}}
                /
                {{humanFileSize volume.plan.quota "GiB"}}
              </EntityField>
              <EntityField data-test-file-storage-volume-cost @label={{t "label.cost"}}>
                {{#if volume.cost}}
                  <CostPlain
                    @currency={{volume.cost.currency}}
                    @price={{volume.cost.price}}
                    @discountedPrice={{volume.cost.total}}
                  />
                {{else}}
                  —
                {{/if}}
              </EntityField>
            </:fields>
          </Entity>
        </:default>
        <:empty>
          <EmptyState
            @icon={{IconsSidebarStorage}}
            @title={{t "file-storage-volume.empty-state.title"}}
            @description={{t "file-storage-volume.empty-state.description"}}
            @points={{array
              (t "file-storage-volume.empty-state.usp-list.shared-between-servers")
              (t "file-storage-volume.empty-state.usp-list.backups-and-bulk-data")
              (t "file-storage-volume.empty-state.usp-list.up-to-16-tib")
            }}
            @learnMoreUrl="https://docs.glesys.com/products/storage/file-storage"
            @learnMoreAriaLabel={{t "file-storage-volume.empty-state.learn-more-aria-label"}}
            @createRoute={{if this.canCreate "file-storage-volumes.create"}}
            @createLabel={{t "file-storage-volume.empty-state.button-create"}}
          >
            {{#if this.canCreate}}
              <p class="mt-2 text-xs text-gray-500" data-test-file-storage-volume-trial>
                {{t "file-storage-volume.empty-state.button-sub-text"}}
              </p>
            {{/if}}
          </EmptyState>
        </:empty>
      </EntityList>
    </PageContent>
  </template>
}
